import { Button } from "@trotroways/ui";
import { cn, formatDistance, formatTime } from "@trotroways/utils";
import {
  ArrowLeftRight,
  Bus,
  CarTaxiFront,
  ChevronDown,
  ChevronLeft,
  Clock,
  Coins,
  Pencil,
  Bookmark,
  BookmarkCheck,
} from "lucide-react";
import { Fragment, memo, useCallback, useState } from "react";
import { FareCorrectionSheet } from "@/components/fare-correction-sheet";
import { useFareConfirmations } from "@/hooks/use-fare-confirmations";
import type { RouteOption } from "@/lib/api";
import { RiWalk } from "./icon/walk-fill";

export interface RouteDetailsPanelProps {
  route: RouteOption;
  onBack: () => void;
  saved?: boolean;
  onToggleSave?: () => void;
}

const ROUTE_LABELS: Record<string, string> = {
  fastest: "Fastest",
  cheapest: "Cheapest",
  shortest: "Fewest Transfers",
  recommended: "Recommended",
};

export const RouteDetailsPanel = memo(function RouteDetailsPanel({
  route,
  onBack,
  saved,
  onToggleSave,
}: RouteDetailsPanelProps) {
  const confidence = route.confidence ?? 0;
  const relBadge =
    confidence >= 0.7
      ? "High reliability"
      : confidence >= 0.4
        ? "Medium reliability"
        : confidence > 0
          ? "Low reliability"
          : null;
  const relColor =
    confidence >= 0.7
      ? "text-green-400"
      : confidence >= 0.4
        ? "text-amber-400"
        : "text-red-400";
  const label = ROUTE_LABELS[route.type] || route.title;
  const hasTrotro = route.steps.some((s) => s.type === "trotro");

  const { getConfirmation, updateFare, getEffectiveFare } =
    useFareConfirmations(route.steps);
  const [fareSheetStepId, setFareSheetStepId] = useState<string | null>(null);
  const [expandedStopsStepId, setExpandedStopsStepId] = useState<string | null>(
    null,
  );

  const handleFareConfirm = useCallback(
    (stepId: string) => (amount: number, reason?: string) =>
      updateFare(stepId, amount, reason),
    [updateFare],
  );

  const fareSheetStep = fareSheetStepId
    ? route.steps.find((s) => s.id === fareSheetStepId)
    : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="size-8 -ml-2 rounded-full"
          onClick={onBack}
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <h3 className="font-semibold">{label}</h3>
        {onToggleSave && (
          <button
            type="button"
            onClick={onToggleSave}
            aria-label={saved ? "Remove saved route" : "Save route"}
            title={saved ? "Remove saved route" : "Save route"}
            className="ml-auto rounded-full p-1.5 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
          >
            {saved ? (
              <BookmarkCheck className="size-4 text-primary" />
            ) : (
              <Bookmark className="size-4" />
            )}
          </button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 p-3 bg-muted/50 rounded-lg">
        <div className="flex flex-col items-center">
          <Clock className="w-4 h-4 text-primary mb-1" />
          <span className="text-sm font-semibold">
            {formatTime(route.totalTime)}
          </span>
          <span className="text-xs text-muted-foreground">Time</span>
        </div>
        <div className="flex flex-col items-center">
          <Coins className="w-4 h-4 text-primary mb-1" />
          <span className="text-sm font-semibold">
            {route.totalPrice > 0
              ? `₵${route.totalPrice.toFixed(2)}`
              : hasTrotro
                ? "—"
                : "₵0.00"}
          </span>
          <span className="text-xs text-muted-foreground">Cost</span>
        </div>
        <div className="flex flex-col items-center">
          <ArrowLeftRight className="w-4 h-4 text-primary mb-1" />
          <span className="text-sm font-semibold">{route.transfers}</span>
          <span className="text-xs text-muted-foreground">Transfers</span>
        </div>
      </div>

      {relBadge && (
        <div className={`text-xs font-medium ${relColor}`}>
          {relBadge}
          {route.totalP10 != null && route.totalP90 != null && (
            <span className="ml-1 text-white/40">
              ({formatTime(route.totalP10)}–{formatTime(route.totalP90)})
            </span>
          )}
        </div>
      )}

      <div>
        <h4 className="text-sm font-medium mb-5">Journey Steps</h4>
        <div className="space-y-0 max-h-[50vh] overflow-y-auto scrollbar-hide">
          {route.steps.map((step, index) => {
            const isLast = index === route.steps.length - 1;
            const isWalking = step.type === "walking";
            const isInformal = !isWalking && step.scheduled === false;
            const lineColor = step.color || "#2EC4B6";
            const stepConf =
              step.fare && step.fare > 0 ? getConfirmation(step.id) : null;

            return (
              <Fragment key={step.id}>
                {isWalking ? (
                  <div className="flex gap-3 py-3.5">
                    <div className="w-7 shrink-0 flex justify-center">
                      <RiWalk className="size-7 text-white/80" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[14px] font-medium text-white">
                        Walk to {step.to || "destination"}
                      </p>
                      <p className="text-[13px] text-white/40 mt-1">
                        {step.distance > 0 &&
                          `${formatDistance(step.distance)} · `}
                        {formatTime(step.duration)}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-3 py-3.5 items-stretch group">
                    <div className="w-7 shrink-0 flex flex-col items-center">
                      <div
                        className="size-7 rounded-lg flex items-center justify-center shrink-0"
                        style={{ backgroundColor: `${lineColor}33` }}
                      >
                        {isInformal ? (
                          <CarTaxiFront className="size-4" style={{ color: lineColor }} />
                        ) : (
                          <Bus className="size-3.5" style={{ color: lineColor }} />
                        )}
                      </div>
                      <div className="flex-1 flex flex-col items-center justify-between py-0.5">
                        <div
                          className={cn(
                            "flex-1",
                            isInformal
                              ? "border-l border-dashed"
                              : "w-2px bg-white/15",
                          )}
                          style={isInformal ? { borderColor: `${lineColor}4D` } : undefined}
                        />
                        <div
                          className="size-1.75 rounded-full shrink-0 mb-1"
                          style={{ backgroundColor: lineColor }}
                        />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col justify-between">
                      <div className="flex justify-between gap-2 items-baseline">
                        <p className="text-[14px] font-medium text-white">
                          Take Trotro
                        </p>
                        <span className="text-sm  font-medium whitespace-nowrap shrink-0 flex items-center gap-1.5 tabular-nums cursor-pointer" onClick={() => setFareSheetStepId(step.id)}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFareSheetStepId(step.id);
                            }}
                            className="opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                          >
                            <Pencil className="size-3.5" />
                          </button>
                          <span
                            title={
                              stepConf
                                ? `Confirmed by ${stepConf.confirmedCount} ${stepConf.label}`
                                : undefined
                            }
                          >
                            {(() => {
                              const eff = getEffectiveFare(
                                step.id,
                                step.fare,
                              );
                              return eff !== null
                                ? `₵${eff.toFixed(2)}`
                                : "—";
                            })()}
                          </span>
                        </span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap my-1">
                        <span className="text-xs text-white/60 font-medium">
                          {step.from || "Start"} ·
                        </span>

                        {(step.routeName || step.routeNumber) && (
                          <span className="text-xs text-white/60 font-medium">
                            {step.routeNumber}
                          </span>
                        )}
                        <span className="text-xs text-white/40 whitespace-nowrap">
                          {step.distance > 0 && (
                            <> · {formatDistance(step.distance)}</>
                          )}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedStopsStepId(
                            expandedStopsStepId === step.id ? null : step.id,
                          )
                        }
                        className="flex items-center gap-1 text-xs text-white/50 hover:text-white/70 transition-colors my-3"
                      >
                        <ChevronDown
                          className={cn(
                            "size-3 transition-transform",
                            expandedStopsStepId === step.id && "rotate-180",
                          )}
                        />
                        {step.stops && step.stops.length > 0
                          ? `Go ${step.stops.length} stops`
                          : `Go ${Math.max(1, Math.round(step.distance / 400))} stops`} ({formatTime(step.duration, "long")})
                      </button>
                      {expandedStopsStepId === step.id && (
                        <div className="mb-1 ml-4 space-y-px">
                          {(step.stops && step.stops.length > 0
                            ? step.stops
                            : Array.from(
                              {
                                length: Math.max(
                                  1,
                                  Math.round(step.distance / 400),
                                ),
                              },
                              (_, i) => `Stop ${i + 1}`,
                            )
                          ).map((name, i) => (
                            // biome-ignore lint/suspicious/noArrayIndexKey: static list with no stable IDs
                            <p key={i} className="text-xs text-white/40">
                              {name}
                            </p>
                          ))}
                        </div>
                      )}
                      <p className="text-[14px] font-medium text-white">
                        {step.to || "End"}
                      </p>
                    </div>
                  </div>
                )}
                {!isLast && <div className="ml-10 border-t border-white/10" />}
              </Fragment>
            );
          })}
          {fareSheetStep && (
            <FareCorrectionSheet
              open={fareSheetStepId !== null}
              onOpenChange={(open) => {
                if (!open) setFareSheetStepId(null);
              }}
              currentFare={fareSheetStep.fare ?? 0}
              onConfirm={handleFareConfirm(fareSheetStep.id)}
            />
          )}
        </div>
      </div>
    </div>
  );
});
